import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '~/components/Button';
import { BreadcrumbsResourcePage } from '~/components/ResourcePage';
import { VerticalTabs, VerticalTabsLayout } from '~/components/VerticalTabs';
import { ResourceLoader, controllableUseResource } from '~/lib/loader';
import { useAbility } from '~/lib/ability';
import { Intent } from '~/lib/intent';
import { useCopyToClipboard } from '~/lib/useCopyToClipboard';
import { AppAdsAbility } from '~/modules/AppAds/ability';
import { fetchSellerDomains, fetchMismatchedLines } from '~/modules/AppAds/api';
import { AppAdsNavigation } from '~/modules/AppAds/components/AppAdsNavigation';
import { useSelectedPublisher } from '~/modules/AppAds/components/SelectedPublisherContext';
import { IAppAdsMissingLine } from '~/modules/AppAds/models/app-ads';
import { ID } from '~/lib/model';
import { useFocusOnMount } from '~/lib/useFocusOnMount';
import { SelectSource } from '~/modules/Bidders/components/SelectSource';
import style from './index.module.scss';

const relationshipDiffers = (line: IAppAdsMissingLine) =>
  (line.accountRelationship || '').toUpperCase() !== (line.actualRelationship || '').toUpperCase();

const certificationDiffers = (line: IAppAdsMissingLine) =>
  (line.certificationId || '').toLowerCase() !== (line.actualCertificationId || '').toLowerCase();

interface ILineToken {
  value: string;
  changed: boolean;
}

const currentTokens = (line: IAppAdsMissingLine): ILineToken[] =>
  [
    { value: line.adSystemDomain, changed: false },
    { value: line.publisherAccountId, changed: false },
    { value: line.actualRelationship || '', changed: relationshipDiffers(line) },
    { value: line.actualCertificationId || '', changed: certificationDiffers(line) },
  ].filter((t) => t.value.length > 0);

const correctTokens = (line: IAppAdsMissingLine): ILineToken[] =>
  [
    { value: line.adSystemDomain, changed: false },
    { value: line.publisherAccountId, changed: false },
    { value: line.accountRelationship || '', changed: relationshipDiffers(line) },
    { value: line.certificationId || '', changed: certificationDiffers(line) },
  ].filter((t) => t.value.length > 0);

const tokensToText = (tokens: ILineToken[]) => tokens.map((t) => t.value).join(',');

const DiffLine = ({
  sign,
  tone,
  tokens,
}: {
  sign: string;
  tone: 'remove' | 'add';
  tokens: ILineToken[];
}) => {
  const [copied, copy] = useCopyToClipboard();
  const text = tokensToText(tokens);

  return (
    <div className={[style.diffLine, tone === 'remove' ? style.diffRemove : style.diffAdd].join(' ')}>
      <span className={style.diffSign}>{sign}</span>
      <code className={style.diffCode}>
        {tokens.map((token, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className={style.diffComma}>,</span>}
            {token.changed ? (
              <span className={style.diffTokenChanged}>{token.value}</span>
            ) : (
              token.value
            )}
          </React.Fragment>
        ))}
      </code>
      <Button
        small
        className={style.diffCopyBtn}
        intent={copied ? Intent.SUCCESS : Intent.DEFAULT}
        leftIcon={<i className={`fa fa-${copied ? 'check' : 'copy'}`} />}
        onClick={() => copy(text)}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
};

const MismatchCard = ({ line }: { line: IAppAdsMissingLine }) => {
  const changedFields = [
    relationshipDiffers(line) && 'relationship',
    certificationDiffers(line) && 'certification id',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className={style.diffCard}>
      {changedFields && <div className={style.diffCardHeader}>differs in {changedFields}</div>}
      <DiffLine sign="−" tone="remove" tokens={currentTokens(line)} />
      <DiffLine sign="+" tone="add" tokens={correctTokens(line)} />
    </div>
  );
};

export const MismatchedLinesView = () => {
  const ability = useAbility(AppAdsAbility);
  const isPublisherMode = ability.isSeller() || ability.isSelfServe();
  const sellerId = ability.sellerId && ability.sellerId();

  const { selectedPublisherId, setSelectedPublisherId } = useSelectedPublisher();
  const [activeDomain, setActiveDomain] = useState<string | undefined>(undefined);
  const contentRef = useFocusOnMount<HTMLDivElement>(isPublisherMode);

  const publisherId = isPublisherMode ? sellerId : selectedPublisherId;

  const domainsResult = controllableUseResource(() => {
    if (publisherId) {
      return fetchSellerDomains(publisherId);
    }
    return Promise.resolve([]);
  }, []);

  useEffect(() => {
    if (publisherId) {
      domainsResult.fetch();
    }
  }, [publisherId]);

  const linesQuery = useQuery({
    queryKey: ['app-ads-mismatched-lines', publisherId, activeDomain],
    queryFn: () => fetchMismatchedLines(publisherId, activeDomain as string),
    enabled: Boolean(publisherId && activeDomain),
  });

  const linesResult = {
    isPending: linesQuery.isPending,
    resource: linesQuery.data ?? [],
    setResource: () => undefined,
    fetch: () => void linesQuery.refetch(),
  };

  const domainTabs = useMemo(() => {
    const unique = [...new Set(domainsResult.resource || [])].sort();
    return unique.map((domain) => ({
      key: domain,
      id: domain,
      title: domain,
      tooltip: domain,
    }));
  }, [domainsResult.resource]);

  useEffect(() => {
    if (!activeDomain && domainTabs.length > 0) {
      setActiveDomain(domainTabs[0].id);
    }
  }, [domainTabs]);

  return (
    <ResourceLoader result={domainsResult} message="Loading domains">
      {({ resource: domains, error: domainsError }) => (
        <BreadcrumbsResourcePage
          title="Mismatched lines"
          toolbar={
            !isPublisherMode && (
              <SelectSource
                placeholder="Select seller"
                searchable
                combobox
                autoFocus
                className={style.publisherSelect}
                value={selectedPublisherId}
                onChange={(value) => {
                  setSelectedPublisherId(value as ID);
                  setActiveDomain(undefined);
                }}
              />
            )
          }
        >
          <AppAdsNavigation />
          <div className={`layout-content-box ${style.contentFocusable}`} ref={contentRef} tabIndex={-1}>
            {!isPublisherMode && selectedPublisherId == null ? (
              <div className={style.placeholderNote}>Select seller to display domains</div>
            ) : domainsError ? (
              <div className={style.errorNote}>
                Could not load domains.
                <Button intent={Intent.DEFAULT} onClick={() => domainsResult.fetch()}>
                  Retry
                </Button>
              </div>
            ) : !domains?.length ? (
              <div className={style.placeholderNote}>No domains available for this publisher</div>
            ) : (
              <VerticalTabsLayout
                sidebar={
                  <VerticalTabs
                    items={domainTabs}
                    activeId={activeDomain}
                    onChange={setActiveDomain}
                    autoFocusKey={isPublisherMode ? undefined : selectedPublisherId}
                  />
                }
              >
                {!activeDomain ? (
                  <div className={style.placeholderNote}>Select a domain to view mismatched lines</div>
                ) : linesQuery.isError ? (
                  <div className={style.errorNote}>
                    Could not load mismatched lines for {activeDomain}.
                    <Button intent={Intent.DEFAULT} onClick={() => linesQuery.refetch()}>
                      Retry
                    </Button>
                  </div>
                ) : (
                  <ResourceLoader
                    result={linesResult}
                    message={`Loading mismatched lines for ${activeDomain}`}
                  >
                    {({ resource: lines }) =>
                      (lines || []).length ? (
                        <div className={style.mismatchList}>
                          <div className={style.mismatchLegend}>
                            Each block shows the line currently in your file and how it should look.
                            Copy the{' '}
                            <span className={style.legendRemove}>− current line</span> to find it in your
                            app-ads.txt, then replace it with the{' '}
                            <span className={style.legendAdd}>+ correct line</span>.
                          </div>
                          {(lines || []).map((line, i) => (
                            <MismatchCard
                              key={`${line.adSystemDomain}-${line.publisherAccountId}-${i}`}
                              line={line}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className={style.placeholderNote}>
                          No mismatched lines for this domain
                        </div>
                      )
                    }
                  </ResourceLoader>
                )}
              </VerticalTabsLayout>
            )}
          </div>
        </BreadcrumbsResourcePage>
      )}
    </ResourceLoader>
  );
};
